import { ReactNode, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { sendTransactionReceipt } from 'api/transactions';
import * as Dialog from 'components/dialog';
import { ResendItemDialogContext } from 'components/resend-item-dialog';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { useForceRender } from 'customHooks/useForceRender';
import { GivebutterAxiosError } from 'types/error';

interface ResendReceiptDialogRootProps {
    children: ReactNode;
}

const ResendReceiptDialog = (props: ResendReceiptDialogRootProps) => {
    const { children } = props;
    const { data: transaction } = useCurrentTransactionOrById();
    const { success, error } = useToast();

    const { mutate: resendEmail, isPending: isLoading } = useMutation({
        mutationFn: (email: string) =>
            sendTransactionReceipt(transaction?.id, {
                email,
            }),
        onSuccess: (_, email) => {
            success(`Resent receipt to ${email}`);
            forceRender();
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to resend receipt');
        },
    });

    const { key, forceRender } = useForceRender();

    const value = useMemo(
        () => ({
            itemType: 'receipt' as const,
            email: transaction?.email,
            resendEmail,
            forceRender,
            isLoading,
            isDisabled: false,
        }),
        [transaction?.email, resendEmail, forceRender, isLoading],
    );

    return (
        <ResendItemDialogContext.Provider value={value}>
            <Dialog.Root key={key}>{children}</Dialog.Root>
        </ResendItemDialogContext.Provider>
    );
};

const Root = ResendReceiptDialog;

export {
    Root,
    //
    ResendReceiptDialog,
};
